// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Render tiers (ARCH-010).
//
// One machine definition, four fidelity tiers. This file is only the **vocabulary and the
// budgets** — which tier exists and what each is allowed to spend. Choosing one from measured
// capability is M3-35 and stepping down under load is M3-36; both need to agree on this table,
// and a table each is how they would come to disagree.
//
// The one rule that outranks every number here: a tier changes what the player *sees*. It never
// changes what they *play* (ARCH-012, PH-022).

export const RENDER_TIERS = ['xr', 'high', 'medium', 'low'] as const
export type RenderTier = (typeof RENDER_TIERS)[number]

export interface TierBudget {
  /**
   * Largest device-pixel ratio worth honouring.
   *
   * A phone reporting 3 is not asking for nine times the fragments; it is describing a screen
   * whose pixels are too small to resolve individually. Past about 2 the extra work buys
   * nothing a player can see and costs everything a thermal governor will later take away.
   */
  readonly pixelRatioMax: number
  /**
   * Ceiling on total drawing-buffer pixels.
   *
   * Capping the ratio alone does not bound the work, which is the mistake this exists to avoid:
   * a 4K display at ratio 1 is 8.3 megapixels and will flatten a laptop GPU, while a phone at
   * ratio 3 on a small panel is a third of that and perfectly comfortable. The budget is what
   * the hardware actually pays.
   */
  readonly pixelBudget: number
  /** Dynamic lights this tier may use. Inserts are never lights (ARCH-011). */
  readonly dynamicLights: number
  /** Frame budget in milliseconds, which is what the M3-36 governor measures against. */
  readonly frameBudgetMs: number
}

export const TIER_BUDGETS: Readonly<Record<RenderTier, TierBudget>> = {
  // 72–90 Hz, two eyes. The frame budget is the harshest in the product and the pixel budget
  // is per eye — a Quest 3 eye buffer is roughly 2064 × 2208.
  xr: { pixelRatioMax: 1.2, pixelBudget: 4_600_000, dynamicLights: 1, frameBudgetMs: 11.1 },
  high: { pixelRatioMax: 2, pixelBudget: 5_000_000, dynamicLights: 3, frameBudgetMs: 16.7 },
  medium: { pixelRatioMax: 1.75, pixelBudget: 2_600_000, dynamicLights: 2, frameBudgetMs: 16.7 },
  low: { pixelRatioMax: 1.25, pixelBudget: 1_300_000, dynamicLights: 1, frameBudgetMs: 16.7 },
}

/** The next tier down, or `undefined` at the bottom. `xr` never steps: it is a device, not a level. */
export function tierBelow(tier: RenderTier): RenderTier | undefined {
  if (tier === 'high') return 'medium'
  if (tier === 'medium') return 'low'
  return undefined
}
